import { useEffect, useState, Component } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { Layout } from "./components/Layout";
import { AdminDashboard } from "./components/AdminDashboard";
import { LoginPage } from "./components/LoginPage";
import { AdminSetup } from "./components/AdminSetup";
import { SchedulePage } from "./components/SchedulePage";
import { WatchPage } from "./components/WatchPage";
import { ChannelPage } from "./components/ChannelPage";
import { RecordingsPage } from "./components/RecordingsPage";
import { RecordingPlayerPage } from "./components/RecordingPlayerPage";
import { CareersPage } from "./components/CareersPage";
import { SponsorSignupPage } from "./components/SponsorSignupPage";
import { SponsorLoginPage } from "./components/SponsorLoginPage";
import { SponsorDashboard } from "./components/SponsorDashboard";
import { EventPage } from "./components/EventPage";
import { BracketManagerPage } from "./components/BracketManagerPage";
import { UserSignupPage } from "./components/UserSignupPage";
import { UserLoginPage } from "./components/UserLoginPage";
import { ForgotPasswordPage } from "./components/ForgotPasswordPage";
import { UserAccountPage } from "./components/UserAccountPage";
import { WatchPartyPage } from "./components/WatchPartyPage";
import { NewsPage } from "./components/NewsPage";
import { NewsArticlePage } from "./components/NewsArticlePage";
import { WriterSetupPage, WriterLoginPage } from "./components/WriterAuth";
import { WriterPortal } from "./components/WriterPortal";
import { PageTitleManager } from "./hooks/usePageTitle";
import { HomePageNew } from "./components/HomePageNew";
import { CustomPageRenderer } from "./components/CustomPageRenderer";
import { PhotosPage } from "./components/PhotosPage";
import { PhotoDetailPage } from "./components/PhotoDetailPage";
import { ContactPage } from "./components/ContactPage";
import { PhotographerSignup } from "./components/PhotographerSignup";
import { PhotographerLogin } from "./components/PhotographerLogin";
import { PhotographerPortal } from "./components/PhotographerPortal";
import { PodcastsPage } from "./components/PodcastsPage";
import { RecordBookPage } from "./components/RecordBookPage";
import { ComingSoonPage } from "./components/ComingSoonPage";
import { GamePage } from "./components/GamePage";
import { GameRecapPage } from "./components/GameRecapPage";
import { UnifiedGamePage } from "./components/UnifiedGamePage";
import { ChannelsPage } from "./components/ChannelsPage";
import { ChannelHomePage } from "./components/ChannelHomePage";
import { StaffPage } from "./components/StaffPage";
import { DynamicEventPage } from "./components/DynamicEventPage";
import { LeaguePage } from "./components/LeaguePage";
import { LeaguesDirectoryPage } from "./components/LeaguesDirectoryPage";
import { TeamPage } from "./components/TeamPage";
import { PlaylistManagerPage } from "./components/admin/PlaylistManagerPage";
import { GuidePage } from "./components/GuidePage";
import { ASNLoader } from "./components/ASNLoader";
import { ConferenceNetworkPage } from "./pages/ConferenceNetworkPage";
import { ChannelSettingsPage } from "./pages/ChannelSettingsPage";
import MultiViewPage from "./pages/MultiViewPage";
import EventsPage from "./components/EventsPage";
import { ASNPlusPage } from "./pages/ASNPlusPage";
import { PlusHubPage } from "./pages/PlusHubPage";
import { TriviaPage } from "./components/TriviaPage";
import MeetingPage from "./components/MeetingPage";
import ProductionStudio from "./components/ProductionStudio";

// Use environment variable or fallback to empty string for relative URLs in production
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

// Axios interceptor: automatically append dev user token for broadcast requests
axios.interceptors.request.use((config) => {
  try {
    const userData = JSON.parse(localStorage.getItem("userData") || "null");
    const userToken = localStorage.getItem("userToken");
    if (userData?.role === "developer" && userToken && config.url?.includes("/broadcasts/")) {
      const url = new URL(config.url, window.location.origin);
      url.searchParams.set("include_dummy", "true");
      config.url = url.toString();
      config.headers = config.headers || {};
      if (!config.headers["Authorization"]) {
        config.headers["Authorization"] = `Bearer ${userToken}`;
      }
    }
  } catch {}
  return config;
});

// Error Boundary to catch React rendering errors
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[ASN] React Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-black flex flex-col items-center justify-center p-4">
          <h1 className="text-white text-2xl mb-4">Something went wrong</h1>
          <p className="text-zinc-400 mb-6 text-center max-w-md">
            The page encountered an error. Please try refreshing.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-2 bg-orange-500 text-white font-bold uppercase hover:bg-orange-600"
          >
            Refresh Page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Homepage Component
const HomePage = () => {
  return (
    <Layout>
      <HomePageNew />
    </Layout>
  );
};

// Wrapper for pages that need standard layout
const PageWithLayout = ({ Component, ...props }) => (
  <Layout>
    <Component {...props} />
  </Layout>
);

// Pages without header/ticker (Admin, Login pages)
const AdminPage = () => (
  <Layout showTicker={false} showHeader={false} showFooter={false}>
    <AdminDashboard />
  </Layout>
);

const BracketEditorPage = () => (
  <Layout showTicker={false} showHeader={false} showFooter={false}>
    <BracketManagerPage />
  </Layout>
);

const LoginWrapper = () => (
  <Layout showTicker={false} showHeader={false} showFooter={false}>
    <LoginPage />
  </Layout>
);

// Coming Soon Wrapper - checks if site is in coming soon mode
const ComingSoonWrapper = ({ children }) => {
  const [comingSoonEnabled, setComingSoonEnabled] = useState(null);
  const [hasBypassPassword, setHasBypassPassword] = useState(false);
  const [bypassed, setBypassed] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user has already bypassed
    const bypassToken = localStorage.getItem('coming_soon_bypass');
    if (bypassToken === 'true') {
      setBypassed(true);
    }
    
    const checkComingSoon = async () => {
      try {
        // Add timeout to prevent hanging
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);
        
        const response = await axios.get(`${API}/settings/coming-soon/status`, {
          signal: controller.signal
        });
        clearTimeout(timeoutId);
        setComingSoonEnabled(response.data.enabled);
        setHasBypassPassword(response.data.has_bypass_password);
      } catch (error) {
        console.error("Error checking coming soon status:", error);
        // On any error (timeout, network, etc.), proceed to show the site
        setComingSoonEnabled(false);
      } finally {
        setLoading(false);
      }
    };
    
    // Add a fallback timeout to ensure we never stay in loading forever
    const fallbackTimeout = setTimeout(() => {
      if (loading) {
        console.warn('[ASN] Coming soon check timed out, proceeding');
        setComingSoonEnabled(false);
        setLoading(false);
      }
    }, 6000);
    
    checkComingSoon();
    
    return () => clearTimeout(fallbackTimeout);
  }, []);

  const handleBypass = () => {
    localStorage.setItem('coming_soon_bypass', 'true');
    setBypassed(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <ASNLoader size="fullscreen" />
      </div>
    );
  }

  if (comingSoonEnabled && !bypassed) {
    return <ComingSoonPage hasBypassPassword={hasBypassPassword} onBypass={handleBypass} />;
  }

  return children;
};

function App() {
  return (
    <ErrorBoundary>
      <div className="App">
        <BrowserRouter>
          <PageTitleManager />
          <Routes>
            {/* Coming Soon Preview (always accessible) */}
            <Route path="/coming-soon-preview" element={<ComingSoonPage />} />
            
            {/* Admin routes - always accessible (bypass coming soon) */}
            <Route path="/login" element={<LoginWrapper />} />
            <Route path="/admin/setup" element={<AdminSetup />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/admin/event/:eventId/brackets" element={<BracketEditorPage />} />
          <Route path="/admin/channel/:channelId/playlist" element={<PlaylistManagerPage />} />
          <Route path="/admin/channel/:channelId/settings" element={<ChannelSettingsPage />} />
          
          {/* All public routes wrapped with ComingSoonWrapper */}
          <Route path="/" element={<ComingSoonWrapper><HomePage /></ComingSoonWrapper>} />
          
          {/* Main Pages with Layout */}
          <Route path="/schedule" element={<ComingSoonWrapper><Layout><SchedulePage /></Layout></ComingSoonWrapper>} />
          <Route path="/game/:broadcastId" element={<ComingSoonWrapper><Layout><UnifiedGamePage /></Layout></ComingSoonWrapper>} />
          {/* Legacy routes redirect to unified game page */}
          <Route path="/game-recap/:broadcastId" element={<ComingSoonWrapper><Layout><UnifiedGamePage /></Layout></ComingSoonWrapper>} />
          <Route path="/watch" element={<ComingSoonWrapper><Layout><WatchPage /></Layout></ComingSoonWrapper>} />
          <Route path="/watch/:channelId" element={<ComingSoonWrapper><Layout><ChannelPage /></Layout></ComingSoonWrapper>} />
          <Route path="/channels" element={<ComingSoonWrapper><Layout><ChannelsPage /></Layout></ComingSoonWrapper>} />
          <Route path="/channels/:channelId" element={<ComingSoonWrapper><Layout><ChannelHomePage /></Layout></ComingSoonWrapper>} />
          <Route path="/staff" element={<ComingSoonWrapper><Layout><StaffPage /></Layout></ComingSoonWrapper>} />
          <Route path="/on-demand" element={<ComingSoonWrapper><Layout><RecordingsPage /></Layout></ComingSoonWrapper>} />
          <Route path="/on-demand/:recordingId" element={<ComingSoonWrapper><Layout><RecordingPlayerPage /></Layout></ComingSoonWrapper>} />
          <Route path="/careers" element={<ComingSoonWrapper><Layout><CareersPage /></Layout></ComingSoonWrapper>} />
          <Route path="/contact" element={<ComingSoonWrapper><Layout><ContactPage /></Layout></ComingSoonWrapper>} />
          
          {/* News Routes */}
          <Route path="/news" element={<ComingSoonWrapper><Layout><NewsPage /></Layout></ComingSoonWrapper>} />
          <Route path="/news/:slug" element={<ComingSoonWrapper><Layout><NewsArticlePage /></Layout></ComingSoonWrapper>} />
          
          {/* Podcasts */}
          <Route path="/podcasts" element={<ComingSoonWrapper><Layout><PodcastsPage /></Layout></ComingSoonWrapper>} />
          
          {/* Record Book */}
          <Route path="/record-book" element={<ComingSoonWrapper><RecordBookPage /></ComingSoonWrapper>} />
          
          {/* Events */}
          <Route path="/events/:eventSlug" element={<ComingSoonWrapper><Layout><EventPage /></Layout></ComingSoonWrapper>} />
          <Route path="/events" element={<ComingSoonWrapper><Layout><EventsPage /></Layout></ComingSoonWrapper>} />
          
          {/* League Directory */}
          <Route path="/leagues" element={<ComingSoonWrapper><Layout><LeaguesDirectoryPage /></Layout></ComingSoonWrapper>} />
          
          {/* League Pages */}
          <Route path="/league/:slug" element={<ComingSoonWrapper><Layout><LeaguePage /></Layout></ComingSoonWrapper>} />
          
          {/* Team Pages */}
          <Route path="/team/:slug" element={<ComingSoonWrapper><Layout><TeamPage /></Layout></ComingSoonWrapper>} />
          
          {/* Sponsor Portal */}
          <Route path="/sponsor/signup" element={<Layout><SponsorSignupPage /></Layout>} />
          <Route path="/sponsor/login" element={<Layout><SponsorLoginPage /></Layout>} />
          <Route path="/sponsor/portal" element={<Layout showTicker={false}><SponsorDashboard /></Layout>} />
          
          {/* Writer Portal */}
          <Route path="/writer/setup" element={<Layout><WriterSetupPage /></Layout>} />
          <Route path="/writer/login" element={<Layout><WriterLoginPage /></Layout>} />
          <Route path="/writer/portal" element={<Layout showTicker={false}><WriterPortal /></Layout>} />
          
          {/* User Authentication */}
          <Route path="/signup" element={<Layout><UserSignupPage /></Layout>} />
          <Route path="/login-user" element={<Layout><UserLoginPage /></Layout>} />
          <Route path="/forgot-password" element={<Layout><ForgotPasswordPage /></Layout>} />
          <Route path="/account" element={<Layout><UserAccountPage /></Layout>} />
          
          {/* ASN Plus */}
          <Route path="/plus" element={<Layout><ASNPlusPage /></Layout>} />
          <Route path="/plus/success" element={<Layout><ASNPlusPage /></Layout>} />
          <Route path="/plus/hub" element={<Layout><PlusHubPage /></Layout>} />
          
          {/* Trivia */}
          <Route path="/trivia" element={<ComingSoonWrapper><Layout><TriviaPage /></Layout></ComingSoonWrapper>} />
          
          {/* Watch Party */}
          <Route path="/watch-party/:partyCode" element={<ComingSoonWrapper><Layout><WatchPartyPage /></Layout></ComingSoonWrapper>} />
          
          {/* Program Guide */}
          <Route path="/guide" element={<ComingSoonWrapper><Layout><GuidePage /></Layout></ComingSoonWrapper>} />
          <Route path="/multiview" element={<ComingSoonWrapper><Layout showTicker={false}><MultiViewPage /></Layout></ComingSoonWrapper>} />
          
          {/* Conference Networks - separate from regular channels */}
          <Route path="/network/:networkSlug" element={<ComingSoonWrapper><ConferenceNetworkPage /></ComingSoonWrapper>} />
          <Route path="/network/:networkSlug/schedule" element={<ComingSoonWrapper><ConferenceNetworkPage /></ComingSoonWrapper>} />
          <Route path="/network/:networkSlug/standings" element={<ComingSoonWrapper><ConferenceNetworkPage /></ComingSoonWrapper>} />
          <Route path="/network/:networkSlug/scores" element={<ComingSoonWrapper><ConferenceNetworkPage /></ComingSoonWrapper>} />
          <Route path="/network/:networkSlug/multiview" element={<ComingSoonWrapper><ConferenceNetworkPage /></ComingSoonWrapper>} />
          
          {/* Photos */}
          <Route path="/photos" element={<ComingSoonWrapper><Layout><PhotosPage /></Layout></ComingSoonWrapper>} />
          <Route path="/photos/:id" element={<ComingSoonWrapper><Layout><PhotoDetailPage /></Layout></ComingSoonWrapper>} />
          <Route path="/galleries" element={<ComingSoonWrapper><Layout><PhotosPage /></Layout></ComingSoonWrapper>} />
          <Route path="/galleries/:id" element={<ComingSoonWrapper><Layout><PhotoDetailPage /></Layout></ComingSoonWrapper>} />
          
          {/* Photographer Portal */}
          <Route path="/photographer/signup" element={<Layout><PhotographerSignup /></Layout>} />
          <Route path="/photographer/login" element={<Layout><PhotographerLogin /></Layout>} />
          <Route path="/photographer/portal" element={<Layout showTicker={false}><PhotographerPortal /></Layout>} />
          
          {/* Meeting Room — no Layout wrapper (full-screen experience) */}
          <Route path="/meeting/:meetingId" element={<MeetingPage />} />

          {/* Production Studio — full-screen, no Layout wrapper */}
          <Route path="/studio" element={<ProductionStudio />} />
          
          {/* Custom Pages - must be last to catch custom slugs */}
          <Route path="/:slug" element={<ComingSoonWrapper><Layout><CustomPageRenderer /></Layout></ComingSoonWrapper>} />
          {/* Wildcard route for nested custom paths (e.g., tournaments/march-madness) */}
          <Route path="/*" element={<ComingSoonWrapper><Layout><CustomPageRenderer /></Layout></ComingSoonWrapper>} />
        </Routes>
      </BrowserRouter>
    </div>
    </ErrorBoundary>
  );
}

export default App;
